import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AxiosError } from "axios"
import { parseISO } from "date-fns"
import {
    formatExtendedFieldsSince,
    formatLoyaltyNumber,
    getLoyaltyAnalyticsPeriodDays,
    getLoyaltyAnalyticsErrorMessage,
    isLoyaltyAnalyticsPeriodTooLong,
    resetFilters,
    selectLoyaltyAnalyticsFilters,
    selectLoyaltyReportsAppliedFiltersCount,
    setIncludeUnassignedStore,
    setIncludeUnknownLevel,
    setLevels,
    setPeriod,
    setStoreIds,
} from "@/features/loyalty-analytics"
import {
    EXPORT_MAX_PERIOD_DAYS,
    EXPORT_MAX_ROWS,
    exportLoyaltyAccruals,
    getLoyaltyLevelBreakdown,
    getLoyaltyStores,
    getLoyaltySummary,
    ILoyaltyLevelBreakdownResponse,
    ILoyaltyStoresResponse,
    ILoyaltySummaryResponse,
} from "@/shared/api/loyalty-analytics"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { PeriodPicker } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { PageHeader } from "@/widgets/page-header"

const ALL_OPTION_VALUE = "__all__"
const UNASSIGNED_OPTION_VALUE = "__unassigned__"
const UNKNOWN_LEVEL_OPTION_VALUE = "__unknown_level__"
type LoadStatus = "idle" | "loading" | "loaded" | "error"

const getDownloadFilename = (contentDisposition: string | undefined, fallback: string): string => {
    if (!contentDisposition) return fallback

    const encoded = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
    if (encoded) {
        try {
            return decodeURIComponent(encoded.replace(/^"|"$/g, ""))
        } catch {
            return encoded
        }
    }

    return contentDisposition.match(/filename="?([^";]+)"?/i)?.[1] || fallback
}

const RetryState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="info_panel flex items-center justify-between gap-4">
        <span className="text-color-danger">{message}</span>
        <button type="button" onClick={onRetry} className="form_handle_button !w-auto px-6">Повторить</button>
    </div>
)

export const LoyaltyReportsPage = () => {
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectLoyaltyAnalyticsFilters)
    const appliedFiltersCount = useAppSelector(selectLoyaltyReportsAppliedFiltersCount)
    const isPeriodTooLong = isLoyaltyAnalyticsPeriodTooLong(filters.period)
    const isExportPeriodTooLong =
        getLoyaltyAnalyticsPeriodDays(filters.period) > EXPORT_MAX_PERIOD_DAYS
    const summaryRequestId = useRef(0)
    const storesRequestId = useRef(0)
    const levelsRequestId = useRef(0)
    const downloadGuard = useRef(false)
    const [summaryStatus, setSummaryStatus] = useState<LoadStatus>("idle")
    const [summary, setSummary] = useState<ILoyaltySummaryResponse | null>(null)
    const [summaryError, setSummaryError] = useState("")
    const [storesStatus, setStoresStatus] = useState<LoadStatus>("idle")
    const [stores, setStores] = useState<ILoyaltyStoresResponse | null>(null)
    const [storesError, setStoresError] = useState("")
    const [levelsStatus, setLevelsStatus] = useState<LoadStatus>("idle")
    const [levelBreakdown, setLevelBreakdown] = useState<ILoyaltyLevelBreakdownResponse | null>(null)
    const [levelsError, setLevelsError] = useState("")
    const [isDownloading, setIsDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState("")
    const [downloadWarning, setDownloadWarning] = useState("")
    const [downloadInfo, setDownloadInfo] = useState("")
    const isExportRowLimitExceeded = summaryStatus === "loaded"
        && Number(summary?.accrualsCount) > EXPORT_MAX_ROWS

    const exportQuery = useMemo(() => ({
        from: filters.period.from,
        to: filters.period.to,
        storeIds: filters.storeIds,
        includeUnassignedStore: filters.includeUnassignedStore,
        levels: filters.levels,
        includeUnknownLevel: filters.includeUnknownLevel,
    }), [
        filters.includeUnassignedStore,
        filters.includeUnknownLevel,
        filters.levels,
        filters.period.from,
        filters.period.to,
        filters.storeIds,
    ])

    const fetchSummary = useCallback(() => {
        const requestId = ++summaryRequestId.current
        if (isPeriodTooLong) {
            setSummaryStatus("idle")
            setSummary(null)
            setSummaryError("")
            return
        }
        setSummaryStatus("loading")
        setSummary(null)
        setSummaryError("")

        getLoyaltySummary(exportQuery)
            .then((response) => {
                if (summaryRequestId.current !== requestId) return
                setSummary(response.data)
                setSummaryStatus("loaded")
            })
            .catch((error) => {
                if (summaryRequestId.current !== requestId) return
                setSummaryStatus("error")
                setSummaryError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [exportQuery, isPeriodTooLong])

    const fetchStores = useCallback(() => {
        const requestId = ++storesRequestId.current
        if (isPeriodTooLong) {
            setStoresStatus("idle")
            setStores(null)
            setStoresError("")
            return
        }
        setStoresStatus("loading")
        setStores(null)
        setStoresError("")

        getLoyaltyStores({ from: filters.period.from, to: filters.period.to })
            .then((response) => {
                if (storesRequestId.current !== requestId) return
                setStores(response.data)
                setStoresStatus("loaded")
            })
            .catch((error) => {
                if (storesRequestId.current !== requestId) return
                setStoresStatus("error")
                setStoresError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [filters.period.from, filters.period.to, isPeriodTooLong])

    const fetchLevels = useCallback(() => {
        const requestId = ++levelsRequestId.current
        if (isPeriodTooLong) {
            setLevelsStatus("idle")
            setLevelBreakdown(null)
            setLevelsError("")
            return
        }
        setLevelsStatus("loading")
        setLevelBreakdown(null)
        setLevelsError("")

        getLoyaltyLevelBreakdown({ from: filters.period.from, to: filters.period.to })
            .then((response) => {
                if (levelsRequestId.current !== requestId) return
                setLevelBreakdown(response.data)
                setLevelsStatus("loaded")
            })
            .catch((error) => {
                if (levelsRequestId.current !== requestId) return
                setLevelsStatus("error")
                setLevelsError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [filters.period.from, filters.period.to, isPeriodTooLong])

    useEffect(() => fetchSummary(), [fetchSummary])
    useEffect(() => fetchStores(), [fetchStores])
    useEffect(() => fetchLevels(), [fetchLevels])

    const storeOptions = useMemo(() => {
        const storeDisplayValues = new Map(stores?.stores.map((store) => [store.id, store.displayValue]) ?? [])
        const loadedIds = new Set(stores?.storeIds ?? [])
        return [
            { value: ALL_OPTION_VALUE, label: "Все точки" },
            ...(stores?.storeIds.map((storeId) => ({
                value: storeId,
                label: storeDisplayValues.get(storeId) || storeId,
            })) ?? []),
            ...filters.storeIds
                .filter((storeId) => !loadedIds.has(storeId))
                .map((storeId) => ({ value: storeId, label: storeId })),
            ...(stores?.hasUnassignedStore || filters.includeUnassignedStore
                ? [{
                    value: UNASSIGNED_OPTION_VALUE,
                    label: "Точка не указана",
                }]
                : []),
        ]
    }, [filters.includeUnassignedStore, filters.storeIds, stores])

    const selectedStoreOptions = storeOptions.filter((option) =>
        (option.value !== ALL_OPTION_VALUE && filters.storeIds.includes(option.value))
        || (option.value === UNASSIGNED_OPTION_VALUE && filters.includeUnassignedStore)
    )

    const levelOptions = useMemo(() => {
        const rows = levelBreakdown?.rows ?? []
        const loadedLevels = new Set(rows.map((row) => String(row.level)))
        return [
            { value: ALL_OPTION_VALUE, label: "Все уровни" },
            ...rows.map((row) => ({
                value: String(row.level),
                label: `${row.level} — ${row.levelName}${row.isDeletedLevel ? " (удалён)" : ""}`,
            })),
            ...filters.levels
                .filter((level) => !loadedLevels.has(String(level)))
                .map((level) => ({ value: String(level), label: `Уровень ${level}` })),
            ...(Number(levelBreakdown?.unknownLevelAccrualsCount ?? 0) > 0 || filters.includeUnknownLevel
                ? [{ value: UNKNOWN_LEVEL_OPTION_VALUE, label: "Уровень не указан (н/д)" }]
                : []),
        ]
    }, [filters.includeUnknownLevel, filters.levels, levelBreakdown])

    const selectedLevelOptions = levelOptions.filter((option) =>
        (option.value === UNKNOWN_LEVEL_OPTION_VALUE && filters.includeUnknownLevel)
        || (
            option.value !== ALL_OPTION_VALUE
            && option.value !== UNKNOWN_LEVEL_OPTION_VALUE
            && filters.levels.includes(Number(option.value))
        )
    )

    const handleDownload = async (event: FormEvent) => {
        event.preventDefault()
        if (
            isPeriodTooLong
            || isExportPeriodTooLong
            || isExportRowLimitExceeded
            || downloadGuard.current
            || summaryStatus !== "loaded"
            || !summary
            || Number(summary.accrualsCount) === 0
        ) {
            return
        }

        downloadGuard.current = true
        setIsDownloading(true)
        setDownloadError("")
        setDownloadWarning("")
        setDownloadInfo("")

        try {
            const response = await exportLoyaltyAccruals(exportQuery)
            const inclusiveFilename = filters.period.from && filters.period.to
                ? `cashback_${filters.period.from}_${filters.period.to}.csv`
                : ""
            const filename = inclusiveFilename
                || getDownloadFilename(response.headers["content-disposition"], "cashback.csv")
            const url = window.URL.createObjectURL(response.data)
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            const isTruncated = String(response.headers["x-result-truncated"]).toLowerCase() === "true"
            const maxRows = response.headers["x-export-max-rows"] || String(EXPORT_MAX_ROWS)
            const maxPeriodDays = response.headers["x-export-max-period-days"] || String(EXPORT_MAX_PERIOD_DAYS)
            const extendedFieldsSince = response.headers["x-extended-fields-since"]
            if (isTruncated) {
                setDownloadWarning(
                    `Экспорт усечён сервером (до ${maxRows} строк / ${maxPeriodDays} дней). `
                    + "Сузьте период или фильтры"
                )
            }
            const extendedFieldsHeader = typeof extendedFieldsSince === "string"
                ? extendedFieldsSince.trim()
                : ""
            if (extendedFieldsHeader.toLowerCase() === "unavailable") {
                setDownloadInfo("Раздельный учёт фиатной и бонусной оплаты недоступен для части периода")
            } else if (extendedFieldsHeader && !Number.isNaN(parseISO(extendedFieldsHeader).getTime())) {
                setDownloadInfo(
                    `Расширенные исторические поля доступны с ${formatExtendedFieldsSince(extendedFieldsHeader)}`
                )
            }
        } catch (error) {
            const status = (error as AxiosError).response?.status
            setDownloadError(status === 422
                ? "Период или объём выгрузки превышает лимит (92 дня / 100 000 строк). Сузьте период и повторите"
                : getLoyaltyAnalyticsErrorMessage(error))
        } finally {
            downloadGuard.current = false
            setIsDownloading(false)
        }
    }

    return (
        <div className="_page_container">
            <PageHeader title="Отчёты" />
            <div className="flexbox">
                <form onSubmit={handleDownload} className="box">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-lg font-bold screen:text-2xl">Выгрузка начислений кэшбэка</h2>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-[#5F666C]">
                                Применено фильтров: {appliedFiltersCount}
                            </span>
                            <button
                                type="button"
                                onClick={() => dispatch(resetFilters())}
                                className="form_cancel_button !h-10 !w-auto px-5"
                            >
                                Сбросить
                            </button>
                        </div>
                    </div>
                    <PeriodPicker
                        value={filters.period}
                        onChange={(period) => dispatch(setPeriod(period))}
                        withInterval={false}
                    />
                    {isExportPeriodTooLong && (
                        <div className="mt-3 text-color-danger">
                            Максимальный период выгрузки — {EXPORT_MAX_PERIOD_DAYS} дня. Сузьте диапазон.
                        </div>
                    )}
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Select
                            isMulti
                            isLoading={storesStatus === "loading"}
                            options={storeOptions}
                            value={selectedStoreOptions}
                            placeholder="Все точки"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                if (values.length === 0 || values.includes(ALL_OPTION_VALUE)) {
                                    dispatch(setStoreIds([]))
                                    dispatch(setIncludeUnassignedStore(false))
                                    return
                                }
                                dispatch(setStoreIds(values.filter(
                                    (value: string) =>
                                        value !== ALL_OPTION_VALUE && value !== UNASSIGNED_OPTION_VALUE
                                )))
                                dispatch(setIncludeUnassignedStore(values.includes(UNASSIGNED_OPTION_VALUE)))
                            }}
                        />
                        <Select
                            isMulti
                            isLoading={levelsStatus === "loading"}
                            options={levelOptions}
                            value={selectedLevelOptions}
                            placeholder="Все уровни"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                if (values.length === 0 || values.includes(ALL_OPTION_VALUE)) {
                                    dispatch(setLevels([]))
                                    dispatch(setIncludeUnknownLevel(false))
                                    return
                                }
                                dispatch(setLevels(values
                                    .filter((value: string) => value !== UNKNOWN_LEVEL_OPTION_VALUE)
                                    .map(Number)))
                                dispatch(setIncludeUnknownLevel(values.includes(UNKNOWN_LEVEL_OPTION_VALUE)))
                            }}
                        />
                    </div>

                    {storesStatus === "error" && (
                        <div className="mt-3"><RetryState message={storesError} onRetry={fetchStores} /></div>
                    )}
                    {storesStatus === "loaded" && stores?.storeIds.length === 0 && !stores.hasUnassignedStore && (
                        <div className="mt-3 text-sm text-[#828282]">нет данных о точках за период</div>
                    )}
                    {stores?.truncated && (
                        <div className="mt-3 rounded-xl bg-[#FFF4D6] p-3 text-[#8A5B00]">
                            Справочник точек усечён серверным лимитом
                        </div>
                    )}
                    {levelsStatus === "error" && (
                        <div className="mt-3"><RetryState message={levelsError} onRetry={fetchLevels} /></div>
                    )}
                    {levelsStatus === "loaded" && levelBreakdown?.rows.length === 0 && (
                        <div className="mt-3 text-sm text-[#828282]">нет активных уровней за период</div>
                    )}
                    {levelBreakdown?.truncated && (
                        <div className="mt-3 rounded-xl bg-[#FFF4D6] p-3 text-[#8A5B00]">
                            Список уровней усечён серверным лимитом
                        </div>
                    )}

                    <div className="mt-5">
                        {summaryStatus === "loading" && (
                            <div className="info_panel flex justify-center"><Loader /></div>
                        )}
                        {summaryStatus === "error" && <RetryState message={summaryError} onRetry={fetchSummary} />}
                        {summaryStatus === "loaded" && Number(summary?.accrualsCount) === 0 && (
                            <NoTableData label="нет данных за период" />
                        )}
                        {summaryStatus === "loaded" && summary && Number(summary.accrualsCount) > 0 && (
                            <div className="info_panel">
                                <div className="text-lg font-semibold">
                                    Ожидаемый объём: {formatLoyaltyNumber(summary.accrualsCount)} строк
                                </div>
                                <div className="mt-2 text-sm text-[#5F666C]">
                                    Подготовка большого отчёта может занять некоторое время
                                </div>
                            </div>
                        )}
                        {summary?.truncated && (
                            <div className="mt-3 rounded-xl bg-[#FFF4D6] p-3 text-[#8A5B00]">
                                Оценка объёма усечена серверным лимитом периода
                            </div>
                        )}
                        {isExportRowLimitExceeded && (
                            <div className="mt-3 text-color-danger">
                                Слишком много строк для выгрузки (макс. 100 000). Сузьте период.
                            </div>
                        )}
                    </div>

                    {downloadError && <div className="mt-4 text-color-danger">{downloadError}</div>}
                    {downloadWarning && (
                        <div className="mt-4 rounded-xl bg-[#FFF4D6] p-4 text-[#8A5B00]">{downloadWarning}</div>
                    )}
                    {downloadInfo && <div className="mt-4 text-sm text-[#5F666C]">{downloadInfo}</div>}
                    <button
                        type="submit"
                        disabled={
                            isDownloading
                            || isPeriodTooLong
                            || isExportPeriodTooLong
                            || isExportRowLimitExceeded
                            || summaryStatus !== "loaded"
                            || !summary
                            || Number(summary.accrualsCount) === 0
                        }
                        className="form_handle_button mt-5 flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isDownloading ? <><span>Формируем CSV</span><Loader /></> : "Скачать CSV"}
                    </button>
                </form>
            </div>
        </div>
    )
}
